import React, { useEffect, useRef, useState } from 'react';
import {
  fetchDashboardOrderSummary,
  fetchDashboardWalletBalance,
  fetchGatewaysData,
  fetchTradersData
} from '../../services/dataApi';
import PageLoader from '../../components/PageLoader';
import {
  CalendarPicker,
  formatDateInput,
  formatSelectedDate,
  getBrowserTimezone,
  getTimezoneOffsetLabel,
  getTimezoneOptions,
  TimePicker,
  TimezonePicker,
  toRangeBoundary
} from '../../components/OrderTools';
import '../Page.css';
import './AdminDashboard.css';

const extractList = (payload) => {
  if (payload && payload.data && Array.isArray(payload.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload && payload.ops && payload.ops.length > 0) {
    const firstOp = payload.ops[0];
    if (firstOp.list && Array.isArray(firstOp.list)) {
      return firstOp.list;
    }
    if (firstOp.data && firstOp.data.list && Array.isArray(firstOp.data.list)) {
      return firstOp.data.list;
    }
  }

  return [];
};

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readBalance = (entity) => {
  const balance = entity.balance || entity.Balance || entity.BALANCE || {};

  if (typeof balance === 'number' || typeof balance === 'string') {
    const total = toNumber(balance);
    return { current: total, pending: 0, total };
  }

  const current = toNumber(balance.current || balance.Current || balance.CURRENT);
  const pending = toNumber(balance.pending || balance.Pending || balance.PENDING);
  const totalRaw = balance.total || balance.Total || balance.TOTAL;
  const total = totalRaw === undefined || totalRaw === null || totalRaw === ''
    ? current + pending
    : toNumber(totalRaw);

  return { current, pending, total };
};

const readGatewayCurrency = (entity) =>
  entity.currency || entity.Currency || entity.CURRENCY || 'Unknown';

const sumGatewayBalancesByCurrency = (items) => {
  const totalsByCurrency = items.reduce((acc, item) => {
    const currency = String(readGatewayCurrency(item) || 'Unknown').trim() || 'Unknown';
    const balance = readBalance(item);

    acc[currency] = (acc[currency] || 0) + balance.total;
    return acc;
  }, {});

  return Object.entries(totalsByCurrency)
    .map(([currency, total]) => ({ currency, total }))
    .filter(({ total }) => total !== 0)
    .sort((a, b) => b.total - a.total);
};

const sumBalances = (items) =>
  items.reduce(
    (acc, item) => {
      const balance = readBalance(item);
      return {
        current: acc.current + balance.current,
        pending: acc.pending + balance.pending,
        total: acc.total + balance.total
      };
    },
    { current: 0, pending: 0, total: 0 }
  );

const isTraderRole = (entity) => {
  const role = String(entity.role || entity.Role || entity.ROLE || '').toLowerCase();
  return role === 'trader';
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatOrderCount = (value) => {
  const count = Number(value || 0);
  return `${count.toLocaleString('en-US')} ${count === 1 ? 'order' : 'orders'}`;
};

const formatPercentage = (part, total) => {
  const numerator = Number(part || 0);
  const denominator = Number(total || 0);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return '0%';
  }

  return `${(numerator / denominator * 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  })}%`;
};

const emptyOrderSummary = {
  payin: { all: 0, completed: 0, allCount: 0, completedCount: 0 },
  payout: { all: 0, completed: 0, allCount: 0, completedCount: 0 }
};

const WALLET_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MIN_VISIBLE_WALLET_BALANCE = 10;

const OrderSummaryCard = ({ title, summary }) => (
  <section className="dashboard-card dashboard-order-card">
    <div className="dashboard-card-header">
      <h3>{title}</h3>
      <span className="dashboard-card-meta">USDT</span>
    </div>
    <div className="dashboard-order-primary">
      <span className="dashboard-balance-label">Completed</span>
      <strong>{formatAmount(summary.completed)} USDT</strong>
      <span className="dashboard-order-count">{formatOrderCount(summary.completedCount)}</span>
    </div>
    <div className="dashboard-order-metrics">
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Total volume</span>
        <strong>{formatAmount(summary.all)} USDT</strong>
        <span className="dashboard-order-count">{formatOrderCount(summary.allCount)}</span>
      </div>
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Completed by count</span>
        <strong>{formatPercentage(summary.completedCount, summary.allCount)}</strong>
      </div>
      <div className="dashboard-order-metric">
        <span className="dashboard-rate-label">Completed by volume</span>
        <strong>{formatPercentage(summary.completed, summary.all)}</strong>
      </div>
    </div>
  </section>
);

const DashboardDateRange = ({
  dateFrom,
  dateTo,
  dateFromTime,
  dateToTime,
  timezone,
  onDateFromChange,
  onDateToChange,
  onDateFromTimeChange,
  onDateToTimeChange,
  onTimezoneChange
}) => {
  const [activePicker, setActivePicker] = useState(null);
  const rangeRef = useRef(null);
  const today = formatDateInput(new Date());
  const timezoneOptions = getTimezoneOptions();
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = formatDateInput(yesterdayDate);
  const monthStart = formatDateInput(new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  ));

  useEffect(() => {
    if (!activePicker) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rangeRef.current?.contains(event.target)) {
        setActivePicker(null);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [activePicker]);

  const changeDate = (target, value) => {
    if (target === 'from') {
      onDateFromChange(value);
      if (dateTo && value > dateTo) onDateToChange(value);
    } else {
      onDateToChange(value);
      if (dateFrom && value < dateFrom) onDateFromChange(value);
    }
  };

  const applyPreset = (from, to) => {
    onDateFromChange(from);
    onDateToChange(to);
    onDateFromTimeChange('00:00:00');
    onDateToTimeChange('23:59:59');
    setActivePicker(null);
  };

  const isFullDayRange = dateFromTime === '00:00:00' && dateToTime === '23:59:59';
  const activePreset = isFullDayRange && dateFrom === today && dateTo === today
    ? 'today'
    : isFullDayRange && dateFrom === yesterday && dateTo === yesterday
      ? 'yesterday'
      : isFullDayRange && dateFrom === monthStart && dateTo === today
        ? 'month'
        : null;

  const renderPicker = (target) => {
    if (!activePicker || activePicker.target !== target) return null;
    const isFrom = target === 'from';

    return (
      <div className="dashboard-date-popover">
        {activePicker.type === 'date' && (
          <CalendarPicker
            value={isFrom ? dateFrom : dateTo}
            max={today}
            onChange={(value) => changeDate(target, value)}
            onClose={() => setActivePicker(null)}
          />
        )}
        {activePicker.type === 'time' && (
          <TimePicker
            value={isFrom ? dateFromTime : dateToTime}
            onChange={isFrom ? onDateFromTimeChange : onDateToTimeChange}
            onClose={() => setActivePicker(null)}
          />
        )}
        {activePicker.type === 'timezone' && (
          <TimezonePicker
            value={timezone}
            options={timezoneOptions}
            onChange={onTimezoneChange}
            onClose={() => setActivePicker(null)}
          />
        )}
      </div>
    );
  };

  const renderField = (target, label, date, time, clear) => (
    <div className="dashboard-range-field">
      <span className="dashboard-range-label">{label}</span>
      <div className="dashboard-range-values">
        <button
          type="button"
          onClick={() => setActivePicker({ type: 'date', target })}
        >
          {date ? formatSelectedDate(date) : 'Choose date'}
        </button>
        {date && (
          <>
            <button
              type="button"
              onClick={() => setActivePicker({ type: 'time', target })}
            >
              {time}
            </button>
            <button
              type="button"
              onClick={() => setActivePicker({ type: 'timezone', target })}
            >
              {getTimezoneOffsetLabel(timezone)}
            </button>
          </>
        )}
      </div>
      {date && (
        <button
          type="button"
          className="dashboard-range-clear"
          onClick={clear}
          aria-label={`Clear ${label.toLowerCase()}`}
        >
          ×
        </button>
      )}
      {renderPicker(target)}
    </div>
  );

  return (
    <div className="dashboard-date-range" ref={rangeRef}>
      <div className="dashboard-range-presets">
        <button
          type="button"
          className={activePreset === 'today' ? 'is-active' : ''}
          onClick={() => applyPreset(today, today)}
        >
          Today
        </button>
        <button
          type="button"
          className={activePreset === 'yesterday' ? 'is-active' : ''}
          onClick={() => applyPreset(yesterday, yesterday)}
        >
          Yesterday
        </button>
        <button
          type="button"
          className={activePreset === 'month' ? 'is-active' : ''}
          onClick={() => applyPreset(monthStart, today)}
        >
          This month
        </button>
      </div>
      {renderField('from', 'Date from', dateFrom, dateFromTime, () => onDateFromChange(''))}
      {renderField('to', 'Date to', dateTo, dateToTime, () => onDateToChange(''))}
    </div>
  );
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tradersSummary, setTradersSummary] = useState({ current: 0, pending: 0, total: 0 });
  const [gatewayBalancesByCurrency, setGatewayBalancesByCurrency] = useState([]);
  const [tradersCount, setTradersCount] = useState(0);
  const [gatewaysCount, setGatewaysCount] = useState(0);
  const [orderSummary, setOrderSummary] = useState(emptyOrderSummary);
  const [summaryDateLabel, setSummaryDateLabel] = useState('');
  const [dateFrom, setDateFrom] = useState(() => formatDateInput(new Date()));
  const [dateTo, setDateTo] = useState(() => formatDateInput(new Date()));
  const [dateFromTime, setDateFromTime] = useState('00:00:00');
  const [dateToTime, setDateToTime] = useState('23:59:59');
  const [dateTimezone, setDateTimezone] = useState(getBrowserTimezone);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [tradersPayload, gatewaysPayload] = await Promise.all([
          fetchTradersData(1, 1000),
          fetchGatewaysData(1, 1000)
        ]);

        const traders = extractList(tradersPayload).filter(isTraderRole);
        const gateways = extractList(gatewaysPayload);

        setTradersSummary(sumBalances(traders));
        setGatewayBalancesByCurrency(sumGatewayBalancesByCurrency(gateways));
        setTradersCount(traders.length);
        setGatewaysCount(gateways.length);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  useEffect(() => {
    let active = true;
    let hasLoaded = false;

    const loadWalletBalance = async () => {
      if (!hasLoaded) setWalletLoading(true);
      setWalletError(null);
      try {
        const result = await fetchDashboardWalletBalance();
        if (active) {
          setWalletSummary(result?.data || null);
          hasLoaded = true;
        }
      } catch (err) {
        if (active) setWalletError(err.message || 'Failed to load wallet balance');
      } finally {
        if (active) setWalletLoading(false);
      }
    };

    loadWalletBalance();
    const refreshTimer = setInterval(loadWalletBalance, WALLET_REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(refreshTimer);
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadOrderSummary = async () => {
      if (!dateFrom || !dateTo) {
        setOrderSummary(emptyOrderSummary);
        setSummaryDateLabel('');
        setSummaryError(null);
        setSummaryLoading(false);
        return;
      }

      const rangeFrom = toRangeBoundary(dateFrom, false, dateTimezone, dateFromTime);
      const rangeTo = toRangeBoundary(dateTo, true, dateTimezone, dateToTime);
      if (!rangeFrom || !rangeTo || Date.parse(rangeFrom) > Date.parse(rangeTo)) {
        setSummaryError('Date from must be earlier than date to');
        setSummaryLoading(false);
        return;
      }

      setSummaryLoading(true);
      setSummaryError(null);

      try {
        const ordersPayload = await fetchDashboardOrderSummary(rangeFrom, rangeTo);
        if (active) {
          setOrderSummary(ordersPayload?.data || emptyOrderSummary);
          setSummaryDateLabel(`${formatSelectedDate(dateFrom)} — ${formatSelectedDate(dateTo)}`);
        }
      } catch (err) {
        if (active) {
          setSummaryError(err.message || 'Failed to load dashboard order summary');
        }
      } finally {
        if (active) {
          setSummaryLoading(false);
        }
      }
    };

    loadOrderSummary();
    return () => {
      active = false;
    };
  }, [dateFrom, dateTo, dateFromTime, dateToTime, dateTimezone]);

  const visibleWallets = Array.isArray(walletSummary?.wallets)
    ? walletSummary.wallets.filter((item) => Number(item.balance || 0) >= MIN_VISIBLE_WALLET_BALANCE)
    : [];

  return (
    <div className="page dashboard-page">
      <div className="page-header">
        <div className="page-heading">
          <span className="dashboard-eyebrow">Operations</span>
          <h1>Dashboard</h1>
          <p>Orders, balances and liquidity.</p>
        </div>
      </div>

      <div className="page-content dashboard-page-content">
        <PageLoader loading={loading || (summaryLoading && !summaryDateLabel)} error={error}>
          <div className="page-data">
            <div className="page-section-header dashboard-order-section-header">
              <div className="page-section-title">
                <span className="dashboard-eyebrow">Overview</span>
                <h2>Order performance</h2>
                <p>Local time · {summaryDateLabel}</p>
              </div>
              <DashboardDateRange
                dateFrom={dateFrom}
                dateTo={dateTo}
                dateFromTime={dateFromTime}
                dateToTime={dateToTime}
                timezone={dateTimezone}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                onDateFromTimeChange={setDateFromTime}
                onDateToTimeChange={setDateToTime}
                onTimezoneChange={setDateTimezone}
              />
            </div>

            {summaryError && <div className="dashboard-summary-error">{summaryError}</div>}

            <div className={`dashboard-grid dashboard-orders-grid${summaryLoading ? ' is-loading' : ''}`}>
              <OrderSummaryCard title="PayIN" summary={orderSummary.payin} />
              <OrderSummaryCard title="PayOUT" summary={orderSummary.payout} />
            </div>

            <div className="page-section-header dashboard-section-header dashboard-balance-section-header">
              <div className="page-section-title dashboard-section-title">
                <span className="dashboard-eyebrow">Liquidity</span>
                <h2>Balances</h2>
                <p>Available and pending funds across the operating network.</p>
              </div>
            </div>

            <div className="dashboard-grid dashboard-balance-grid">
              <section className="dashboard-card dashboard-traders-card">
                <div className="dashboard-card-header">
                  <h3>Traders</h3>
                  <span className="dashboard-card-meta">{tradersCount} active</span>
                </div>
                <div className="dashboard-balance-list">
                  <div className="dashboard-balance-item dashboard-balance-primary is-total">
                    <span className="dashboard-balance-label">Total</span>
                    <span className="dashboard-balance-value">{formatAmount(tradersSummary.total)} USDT</span>
                  </div>
                  <div className="dashboard-balance-split">
                    <div className="dashboard-balance-item is-current">
                      <span className="dashboard-balance-label">Current</span>
                      <span className="dashboard-balance-value is-current">{formatAmount(tradersSummary.current)} USDT</span>
                    </div>
                    <div className="dashboard-balance-item is-pending">
                      <span className="dashboard-balance-label">Pending</span>
                      <span className="dashboard-balance-value is-pending">{formatAmount(tradersSummary.pending)} USDT</span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="dashboard-card dashboard-gateways-card">
                <div className="dashboard-card-header">
                  <h3>Gateways</h3>
                  <span className="dashboard-card-meta">{gatewaysCount} connected</span>
                </div>
                {gatewayBalancesByCurrency.length > 0 ? (
                  <div className={`dashboard-gateway-list ${
                    gatewayBalancesByCurrency.length === 1
                      ? 'is-single'
                      : gatewayBalancesByCurrency.length === 2
                        ? 'is-pair'
                        : 'is-multiple'
                  }`}>
                    {gatewayBalancesByCurrency.map(({ currency, total }) => (
                      <div className="dashboard-gateway-row" key={currency}>
                        <span className="dashboard-balance-label">{currency}</span>
                        <span className="dashboard-balance-value">{formatAmount(total)} {currency}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="dashboard-empty-state">No gateway balances found</div>
                )}
              </section>

            </div>

            <div className="page-section-header dashboard-section-header dashboard-wallet-section-header">
              <div className="page-section-title dashboard-section-title">
                <span className="dashboard-eyebrow">Treasury</span>
                <h2>Wallet reserves</h2>
                <p>TRC20 USDT held across trader and NadalPay wallets.</p>
              </div>
              {walletSummary?.updatedAt && (
                <span className="dashboard-wallet-updated">
                  {walletSummary.stale ? 'Cached' : 'Updated'}{' '}
                  <time className="dashboard-wallet-updated-time">
                    {new Date(walletSummary.updatedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </time>
                </span>
              )}
            </div>

            <section className="dashboard-card dashboard-wallet-card">
              {walletError ? (
                <div className="dashboard-wallet-error">{walletError}</div>
              ) : (
                <>
                  <div className="dashboard-wallet-summary">
                    <div>
                      <span className="dashboard-balance-label">Total TRC20 USDT</span>
                      <strong className="dashboard-wallet-total">{walletLoading ? '…' : `${formatAmount(walletSummary?.total)} USDT`}</strong>
                    </div>
                    <div className="dashboard-wallet-counts">
                      <span><b>{walletLoading ? '…' : visibleWallets.length}</b> shown ≥10</span>
                      <span><b>{walletLoading ? '…' : walletSummary?.fundedWalletCount || 0}</b> funded</span>
                      <span><b>{walletLoading ? '…' : walletSummary?.walletCount || 0}</b> checked</span>
                    </div>
                  </div>

                  {!walletLoading && (
                    visibleWallets.length > 0 ? (
                      <div className="dashboard-wallet-list">
                        {visibleWallets.map((item, index) => (
                          <div className="dashboard-wallet-row" key={item.wallet}>
                            <span className="dashboard-wallet-rank">{index + 1}</span>
                            <div className="dashboard-wallet-identity">
                              <strong>{item.traderName || item.traderLogin || item.traderId}</strong>
                              <span title={item.wallet}>{item.wallet}</span>
                            </div>
                            <strong className="dashboard-wallet-amount">
                              {formatAmount(item.balance)} USDT
                            </strong>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="dashboard-empty-state">No wallets with at least 10 USDT</div>
                    )
                  )}
                </>
              )}
            </section>
          </div>
        </PageLoader>
      </div>
    </div>
  );
};

export default AdminDashboard;
